/*
 * Copyright (C) 2016 - 2024 Vivien Pelenc / CEFE / CNRS
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with this program.  If not, see <https://www.gnu.org/licenses/>.
 */


import React, {useEffect, useRef, useState} from 'react';
import Chart from 'chart.js/auto';
import {getCumul, getMeteo24hData, getMeteoDataDate} from "../ConnectionBDD";
import {rgb} from "plotly.js/src/components/color";

export default function ChartSoilHumidityAndTemperature() {
    const color = 'rgb(227,132,0)';

    const [data1, setdata] = useState([]);
    const [data2, setdata2] = useState([]);
    const [labels, setKeys] = useState([]);
    const [dateDebut, setDateDebut] = useState(new Date(Date.now()));
    const [dateFin, setDateFin] = useState(new Date(Date.now()));
    const [depth, setDepth] = useState(1);
    const [mode , setMode] = useState("Mode avancé");

    useEffect(() => {
        const fetchData = async () => {
            try {
                let response;
                switch (depth) {
                    case 0:

                        response = await getMeteoDataDate(
                            new Date(dateDebut).toLocaleDateString('fr-FR', {
                                day: '2-digit',
                                month: '2-digit',
                                year: 'numeric',
                            }),
                            new Date(dateFin).toLocaleDateString('fr-FR', {
                                day: '2-digit',
                                month: '2-digit',
                                year: 'numeric',
                            }));
                        break;
                    case 1:
                        response = await getMeteo24hData();
                        break;
                }

                const sortedResponse = Object.values(response).sort((a, b) => {
                    // Comparer par date
                    const dateComparison = new Date(a.date) - new Date(b.date);
                    if (dateComparison !== 0) {
                        return dateComparison;
                    }
                    // Si les dates sont identiques, comparer par heure
                    return a.time.localeCompare(b.time);
                });

                const dataArray = sortedResponse.map(value => value.soil_temp);
                setdata(dataArray);
                for (let i = 0; i < dataArray.length; i++) {
                    dataArray[i] = parseFloat(dataArray[i]).toFixed(1);
                }

                var dataArray2 = sortedResponse.map(value => value.soil_hum);


                setdata2(dataArray2);

                const keysArray = sortedResponse.map(value => value.time);
                const keysArray_date = sortedResponse.map(value => value.date);
                keysArray_date.forEach((element, index) => {
                    keysArray_date[index] = new Date(element).toLocaleDateString('fr-FR', {
                        day: '2-digit',
                        month: '2-digit',
                        year: '2-digit',
                    });
                });

                for (let i = 0; i < keysArray.length; i++) {
                    keysArray[i] = keysArray_date[i] + " - " + keysArray[i].substring(0, 5);
                }
                setKeys(keysArray);


                /*
                console.log(response);
                console.log(dataArray);
                console.log(dataArray2);*/

            } catch (error) {
                console.error('Erreur lors de la récupération des données :', error);
            }
        };


        fetchData();

        const refreshData = () => {
            const currentDate = new Date();
            const minutes = currentDate.getMinutes();

            // Si l'heure est xxh01 ou xxh31, actualisez les données
            if (minutes === 1 || minutes === 31) {
                fetchData();
            }
        };

        // Actualisez les données toutes les secondes
        const interval = setInterval(refreshData, 59000);

        // Nettoyez l'intervalle lors du démontage du composant
        return () => clearInterval(interval);

    }, [dateDebut,dateFin]);

    //console.log(data);
    //console.log(labels);

    return (
        <div>
            <Graph labels={labels} data1={data1} data2={data2} color={color} setDateDebut={setDateDebut} dateDebut={dateDebut} setDateFin={setDateFin} dateFin={dateFin} setMode={setMode} mode={mode} setDepth={setDepth} depth={depth}/>
        </div>
    );
}

function Graph({ labels, data1, data2, color, setDateDebut, dateDebut, setDateFin, dateFin , setMode, mode, setDepth, depth}) {
    const canvasRef = useRef(null);
    const chartRef = useRef(null);
    let resizeTimeout = null;

    useEffect(() => {
        const resizeChart = () => {
            if (chartRef.current) {
                chartRef.current.resize();
            }
        };

        const delayedResize = () => {
            if (resizeTimeout) {
                clearTimeout(resizeTimeout);
            }
            resizeTimeout = setTimeout(resizeChart, 100); // Redimensionner après 0.5 sec d'inactivité
        };

        window.addEventListener('resize', delayedResize);

        return () => {
            window.removeEventListener('resize', delayedResize);
            if (chartRef.current) {
                chartRef.current.destroy();
            }
            if (resizeTimeout) {
                clearTimeout(resizeTimeout);
            }
        };
    }, []);
    useEffect(() => {
        if (canvasRef.current) {
            if (chartRef.current) {
                chartRef.current.destroy();
            }
            const ctx = canvasRef.current.getContext('2d');
            chartRef.current = new Chart(ctx, {
                type: 'line',
                data: {
                    labels: labels,
                    datasets: [{
                        label: 'Température', // Nom de la première ligne
                        data: data1, // Données pour la première ligne
                        borderColor: 'rgb(204, 0, 0)', // Couleur de la première ligne
                        borderWidth: 2,
                        fill: false,
                        yAxisID: 'y',
                        pointRadius: 5,
                        pointStyle: false,
                    }, {
                        label: 'Humidité', // Nom de la deuxième ligne
                        data: data2, // Données pour la deuxième ligne
                        borderColor: 'rgb(0, 102, 204)', // Couleur de la deuxième ligne
                        borderWidth: 2,
                        fill: false,
                        yAxisID: 'y1',
                        pointRadius: 5,
                        pointStyle: false,

                    }]
                },
                options: {
                    scales: {
                        y: {
                            beginAtZero: false,
                            position: 'left',
                            ticks: {
                                callback: function(value) {
                                    return value.toFixed(1) + '°C'; // Ajoute un pourcentage après chaque valeur
                                },
                                color: 'rgb(204, 0, 0)', // Mettre en vert les valeurs de l'axe des ordonnées
                            }
                        },
                        y1: {

                            beginAtZero: true,
                            min: 0,
                            max: 200,

                            position: 'right',
                            grid: {
                                drawOnChartArea: false, // only want the grid lines for one axis to show up
                            },
                            ticks: {
                                callback: function(value) {
                                    return value.toFixed(0) + ' Cb'; // Ajoute un pourcentage après chaque valeur
                                },
                                stepSize: 10,
                                color: 'rgb(0, 76, 153)', // Mettre en vert les valeurs de l'axe des ordonnées
                            }
                        },
                        x : {
                            ticks: {
                                autoSkip: true,
                                maxTicksLimit: 10,
                            }
                        }
                    },
                    plugins: {
                        title: {
                            display: true,
                            text: 'Température et Humidité du sol',
                        },
                        legend: {
                            display: true // Afficher la légende
                        },
                        tooltip: {
                            mode: 'index',
                            intersect: false
                        }
                    }
                }
            });

        }
    }, [labels, data1, data2]);

    useEffect(() => {
        const handleResize = () => {
            if (chartRef.current) {
                chartRef.current.resize();
            }
        };

        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    return <div>
        <canvas id="graphique" ref={canvasRef}></canvas>

        {depth===0 && (
            <button onClick={() => {
                setDateDebut(new Date(dateDebut.getTime() - 86400000));
                setDateFin(new Date(dateFin.getTime() - 86400000));
                console.log(dateDebut + " " + dateFin);
            }}>&lt;&lt;</button>)}

        {depth===0 && (
            <input
                type="date"
                id="date-picker"
                value={getFormattedDate(dateDebut)}
                onChange={(event) => setDateDebut(new Date(event.target.value))}
            />)}


        {depth===0 && (
            <button onClick={() => {
                setDateDebut(new Date(Date.now()));
                setDateFin(new Date(Date.now()));
            }
            }>Réinitialiser
            </button>)}


        <button onClick={() => {
            if (mode === "Mode 24h") {
                setMode("Mode avancé");
                setDepth(1);
                setDateDebut(new Date(Date.now()));
                setDateFin(new Date(Date.now()));
            } else {
                setMode("Mode 24h");
                setDepth(0);
                setDateDebut(new Date(Date.now()));
                setDateFin(new Date(Date.now()));

            }
            console.log(mode);

        }}>{mode}</button>

        {depth===0 && (
            <input
                type="date"
                id="date-picker"
                value={getFormattedDate(dateFin)}
                onChange={(event) => setDateFin(new Date(event.target.value))}
            />)}


        {((dateFin.getTime() + 86400000) < Date.now() && depth===0) && <button onClick={() => {
            setDateDebut(new Date(dateDebut.getTime() + 86400000));
            setDateFin(new Date(dateFin.getTime() + 86400000));
            console.log();
        }}>&gt;&gt;</button>}
    </div>;
}

function getFormattedDate(date) {
    // Utiliser toLocaleDateString pour obtenir une date au format jour/mois/année
    const dateParts = date.toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
    }).split('/');

    // Réorganiser les parties pour obtenir le format YYYY-MM-DD
    const day = dateParts[0];
    const month = dateParts[1];
    const year = dateParts[2];

    return `${year}-${month}-${day}`;
}